import React, { useState } from 'react';
import classNames from 'classnames';
import i18next from 'i18next';

import { PaidContent } from 'components/PaidContent';
import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import Link from 'components/Link';
import TitleLogo from 'components/TitleLogo';

import {
  package as PackagePropType,
  packageContext as PackageContextPropType,
  layoutContext as LayoutContextPropType,
} from 'lib/CustomPropTypes';
import {
  aLaCarte,
  insertPaidContent,
  isPaidContentType,
} from 'lib/paidContentUtils';

import ALaCarteItem from './ALaCarteItem';

function renderTitle(title, titleUrl, logoUrl) {
  if (!title && !logoUrl) return null;

  const titleLogo = <TitleLogo logoUrl={logoUrl} title={title} />;
  return (
    <h2 className="alacarte__title" data-test="alacarte__title">
      {titleUrl ? (
        <Link href={titleUrl}>
          {titleLogo}
        </Link>
      ) : titleLogo}
    </h2>
  );
}

function renderDescription(description) {
  if (!description) return null;

  return (
    <p
      className="alacarte__description publico-txt f3 lh-copy fw4"
      data-test="alacarte__description"
    >
      {description}
    </p>
  );
}

function ALaCarte(props, context = {}) {
  const [adLoaded, setAdLoaded] = useState(false);

  const { railContext, vertical } = context;

  const { content } = props;
  const { id, items = [], metadata: packageMetadata } = content;

  // In case of API error, not a valid case
  if (!items || !items.length) return null;

  const {
    description,
    logoUrl,
    seeAllUrl,
    showFeaturedFirstItem,
    title,
    titleUrl,
  } = packageMetadata || {};

  const isInRail = railContext !== null;

  // Use this flag below to swap between shrink sizing and static sizing
  // This can be utilized when Tease List is migrated to this component
  const isLatestStories = false;

  const seeAllText = packageMetadata.seeAllText || i18next.t('See All');

  const hasHeader = title || description;

  let filtered = items
    // filter out any null content
    .filter((item) => item)
    // map package metadata with content metadata
    .map((item) => ({
      ...item,
      metadata: {
        ...packageMetadata,
        ...item.metadata,
      },
    }));

  // Insert paidContent tease
  filtered = insertPaidContent(filtered, aLaCarte, { isInRail, ...context });

  return (
    <div
      className={classNames('pkg alacarte', {
        // Sizing strategy is global modifier as it determines flexbox behavior and height
        'alacarte--shrink-to-fit': isLatestStories,
        // Modify styling based on placement in rail
        'alacarte--in-rail': isInRail,
      })}
      data-test="alacarte"
      data-packageid={id}
    >
      <div className="alacarte__wrapper">
        {hasHeader && (
          <div className="alacarte__top-decoration" data-test="alacarte__top-decoration" />
        )}
        {renderTitle(title, titleUrl, logoUrl)}
        {renderDescription(description)}
        <div className="alacarte__content">
          <div className="alacarte__items-wrapper">
            <ul className="alacarte__items" data-test="alacarte__items">
              {filtered.map((item, i) => (
                (isPaidContentType(item.type)) ? (
                  <PaidContent
                    adLoaded={() => {
                      setAdLoaded(true);
                    }}
                    className={classNames({
                      'alacarte__item--paid-content-placeholder': !adLoaded,
                      alacarte__item: adLoaded,
                    })}
                    key={item.type}
                    type={item.type}
                    vertical={vertical}
                  />
                ) : (
                  <ALaCarteItem
                    featured={i === 0 && showFeaturedFirstItem}
                    item={item}
                    key={item.id}
                  />
                )
              ))}
            </ul>
          </div>
          {seeAllUrl && ( // only show button if URL is set
            <div
              className="alacarte__button-wrapper"
              data-test="alacarte__button-wrapper"
            >
              <ButtonHoverAnimation
                size={isInRail ? 'normal' : 'small'}
                type="link"
                url={seeAllUrl}
                additionalClasses="alacarte__button"
                title={seeAllText}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ALaCarte.propTypes = {
  content: PackagePropType.isRequired,
};

ALaCarte.contextTypes = {
  adsDisabled: LayoutContextPropType.adsDisabled,
  isRailLayout: LayoutContextPropType.isRailLayout,
  pageRoute: LayoutContextPropType.pageRoute,
  railContext: PackageContextPropType.railContext,
  vertical: LayoutContextPropType.vertical,
};

export default ALaCarte;
